import { Injectable } from '@angular/core';
import { MissingTranslationHandler, MissingTranslationHandlerParams, TranslateService } from '@ngx-translate/core';
import { isFirstLevelKey } from '../constants/first-level-keys';
import { isDictionaryMissing } from '../utils/translation-load-failure';
import { announceTranslationLoadFailure, RetryingTranslateLoader } from './retrying-translate-loader';

@Injectable()
export class CustomMissingTranslationHandler implements MissingTranslationHandler {
    // Languages already reported on this page load; the handler runs for every word the library can't find
    private readonly reportedLanguages = new Set<string>();

    handle(params: MissingTranslationHandlerParams): string {
        const key = params.key;

        // NB-5629: a missing word can mean the page's whole language is missing; if so, offer a refresh
        this.reportIfLanguageMissing(params.translateService);
        
        // Apply fallback logic
        return this.extractFallbackLabel(key);
    }

    // Reports only when the page's language has no words at all and its file is no longer downloading.
    // A forgotten translation (words loaded, this one key absent) never reports.
    private reportIfLanguageMissing(translate?: TranslateService): void {
        const lang = translate?.currentLang;
        if (!lang || this.reportedLanguages.has(lang) || RetryingTranslateLoader.isDownloading(lang)) return;
        if (!isDictionaryMissing(translate.translations?.[lang])) return;

        this.reportedLanguages.add(lang);
        // After the current screen update, so the prompt never opens in the middle of one
        setTimeout(() => announceTranslationLoadFailure(lang));
    }

    private extractFallbackLabel(key: string): string {
        if (!key) return key;
        
        // Early return for simple keys without dots
        if (!key.includes('.')) return key;
        
        // Check if the first part is a first-level key
        const firstPart = key.split('.')[0];
        if (!isFirstLevelKey(firstPart)) {
            return key;
        }
        
        // Apply existing fallback logic for non-first-level keys
        // Check if this looks like a translation key (has multiple dots and structured format)
        const dotCount = (key.match(/\./g) || []).length;
        if (dotCount < 2) return key;
        
        // Check if it follows translation key pattern (e.g., 'module.section.key')
        const parts = key.split('.');
        
        // If any part is empty, this is likely not a translation key, return the full key
        if (parts.some(part => part.trim() === '')) return key;
                        
        let result = '';
        
        // Start from the last part and iterate backwards
        for (let i = parts.length - 1; i >= 0; i--) {
            const part = parts[i];
            
            // If the part contains spaces, concatenate it to the right of result
            if (part.includes(' ')) {
                result = part + result;
            } else {
                // Stop when encountering a part without spaces
                break;
            }
        }
        
        const fallback = result || parts[parts.length - 1];
        return fallback;
    }
}